import type { InvitationListItem } from '@/lib/api';

export type InvitationStatus = 'pending' | 'expired';

/** Highlight threshold: invites expiring within this window render in red. */
const EXPIRY_WARNING_MS = 24 * 60 * 60 * 1000;

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Pending while the expiry is in the future, expired once it passes. Status is
 * derived rather than stored so a row flips to Expired without a backend write
 * (TDEV-141).
 */
export function getInvitationStatus(expiresAt: string, now = Date.now()): InvitationStatus {
  return new Date(expiresAt).getTime() > now ? 'pending' : 'expired';
}

/** True when a pending invite is within 24h of expiry — the red-highlight cue. */
export function isExpiringSoon(expiresAt: string, now = Date.now()): boolean {
  const remaining = new Date(expiresAt).getTime() - now;
  return remaining > 0 && remaining <= EXPIRY_WARNING_MS;
}

/**
 * Human-readable time-to-expiry, e.g. "3 days left", "5 hours left",
 * "12 minutes left", or "Expired". Coarse on purpose — the table wants an
 * at-a-glance sense of urgency, not a precise countdown.
 */
export function formatTimeRemaining(expiresAt: string, now = Date.now()): string {
  const remaining = new Date(expiresAt).getTime() - now;
  if (remaining <= 0) return 'Expired';

  const days = Math.floor(remaining / MS_PER_DAY);
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'} left`;

  const hours = Math.floor(remaining / MS_PER_HOUR);
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hour' : 'hours'} left`;

  const minutes = Math.max(1, Math.floor(remaining / MS_PER_MINUTE));
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`;
}

/**
 * Admins (active or suspended — a suspended admin keeps their seat) plus pending
 * (non-expired) invitations count toward the cap. `adminCount` is the full
 * roster length, so suspended admins are already included.
 */
export function countUsedSlots(
  adminCount: number,
  invitations: Pick<InvitationListItem, 'expiresAt'>[],
  now = Date.now()
): number {
  const pending = invitations.filter(
    (inv) => getInvitationStatus(inv.expiresAt, now) === 'pending'
  );
  return adminCount + pending.length;
}
